import {type RumEvent} from '@datadog/browser-rum';

import {ignoredErrorMessages} from '../ignoredErrorMessages';
import {
  THIRD_PARTY_DOMAIN_EXCLUSIONS,
  TTI_DOMAIN_INCLUSIONS,
  TTI_WANDB_ANALYTICS_DOMAIN,
} from './profiler/constants';

// https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/?tab=event#discard-a-frontend-error
const DISCARDED_ERROR_MESSAGES = [
  "'measure' on 'Performance': The mark 'GTM", // Google Tag manager
  'Performance.measure: Given mark name', // Google Tag manager
  'https://jscloud.net/x/25796/inlinks.js',
  ...ignoredErrorMessages,
];

// Errors thrown by browser extensions aren't actionable. Keep the first one per
// session as a signal that an extension is interfering, and drop the rest.
let hasLoggedChromeExtensionError = false;

/**
 * Central discard filter for the RUM `beforeSend` hook. Returns true when an
 * event should not be sent to Datadog: known-noise error messages, repeat
 * Chrome-extension errors, and resource events for third-party trackers (ad
 * pixels, marketing tags, ...) which drown out first-party requests in the
 * resource explorer and session waterfall.
 *
 * @param event - the RUM event passed to `beforeSend`
 * @returns true when the event should be discarded
 */
export function shouldDiscardRumEvent(event: RumEvent): boolean {
  if (event.type === 'error') {
    if (
      DISCARDED_ERROR_MESSAGES.some(msg => event.error.message.includes(msg))
    ) {
      return true;
    }
    if (event.error.stack?.includes('chrome-extension://')) {
      if (hasLoggedChromeExtensionError) {
        return true;
      }
      hasLoggedChromeExtensionError = true;
    }
    return false;
  }

  if (event.type === 'resource') {
    return isThirdPartyTrackerUrl(event.resource.url);
  }

  return false;
}

/**
 * Matches hosts on the shared third-party denylist. A denylist is used instead
 * of a first-party allowlist because customer BYOB storage hosts (arbitrary S3
 * buckets, `wandb-data.cwobject.com`, ...) are real product traffic with
 * unpredictable hostnames.
 *
 * @param url - the resource URL from the RUM event
 * @returns true when the URL belongs to a known third-party tracker
 */
function isThirdPartyTrackerUrl(url: string): boolean {
  // Resources injected by browser extensions surface with an extension scheme
  // and an opaque extension-id host; they're never our traffic.
  if (/^(chrome|moz|safari-web)-extension:/.test(url)) {
    return true;
  }
  const hostname = getUrlHostname(url);
  if (hostname === undefined) {
    return false;
  }
  // Google ad/analytics pings come from www.google.<tld> across ~70 country
  // TLDs; match the prefix instead of listing each one.
  if (hostname.startsWith('www.google.')) {
    return true;
  }

  // whether a hostname matches a domain list by exact host or domain
  // suffix — never by substring, so a short entry like `t.co` can't
  // match hosts that contain it (e.g. `gs.wandbusercontent.com`).
  return THIRD_PARTY_DOMAIN_EXCLUSIONS.some(
    domain => hostname === domain || hostname.endsWith(`.${domain}`)
  );
}

function getUrlHostname(url: string): string | undefined {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return undefined;
  }
}

/**
 * Filter for the RUM `excludedActivityUrls` init option: returns true when a
 * request URL should not count toward the SDK's page-activity / loading-time
 * computation. Only first-party domains (TTI_DOMAIN_INCLUSIONS) count. This is
 * separate from `shouldDiscardRumEvent`, which filters resource *collection*.
 *
 * @param url - the request URL the SDK is evaluating for page activity
 * @returns true when the URL should be excluded from activity tracking
 */
export function shouldExcludeUrlFromPageActivity(url: string): boolean {
  const hostname = getUrlHostname(url);
  return (
    hostname === undefined ||
    // Same exact-host-or-suffix matching as the tracker denylist above.
    !TTI_DOMAIN_INCLUSIONS.some(
      domain => hostname === domain || hostname.endsWith(`.${domain}`)
    ) ||
    // exclude analytics.wandb.ai - this is a special case because it includes wandb.ai, which is allowed
    hostname === TTI_WANDB_ANALYTICS_DOMAIN
  );
}
